"use client";

import React from "react";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { animate, useInView, useMotionValue, type MotionValue } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import RevealText from "@/components/bastian-keller/RevealText";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { MotionDiv, MotionSpan } from "@/components/global/utils/motionTags";
import { parseStat, useCountUp } from "@/components/bastian-keller/statValue";
import { useMotionMode } from "@/components/global/utils/motionMode";
import { SPAN_OFFSET, useReveal, useRevealProgress } from "@/components/global/utils/scrollReveal";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

/** chip, statement, text, CTA — the blocks above the stats */
const FIXED_BLOCKS = 4;

function StatCard({
  stat,
  progress,
  index,
  total,
  reduceMotion,
}: {
  stat: PageBastianKellerSchema["goalsStats"][number];
  progress: MotionValue<number>;
  index: number;
  total: number;
  reduceMotion: boolean;
}) {
  const { reveal, y } = useReveal(progress, index, total, reduceMotion);

  const parsed = parseStat(stat?.value);
  const counts = Boolean(parsed) && !reduceMotion;

  const cardRef = React.useRef<HTMLDivElement>(null);
  const inView = useInView(cardRef as React.RefObject<Element>, { once: true, amount: 0.6 });
  const countProgress = useMotionValue(0);
  const display = useCountUp(countProgress, parsed);

  React.useEffect(() => {
    if (!inView || !counts) return;
    const controls = animate(countProgress, 1, { duration: 1.6, delay: (index - FIXED_BLOCKS) * 0.15, ease: [0.33, 1, 0.68, 1] });
    return () => controls.stop();
  }, [inView, counts, index, countProgress]);

  // the server has no scroll position, so it would render a zero — the authored
  // value stays in the markup until the client takes over
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  return (
    <MotionDiv
      ref={cardRef}
      style={{ opacity: reveal, y }}
      className="flex flex-1 flex-col gap-[1.75rem] bg-[#052920]/[.03] px-[4rem] py-[3rem] [clip-path:polygon(0_0,calc(100%-3.589rem)_0,100%_3.589rem,100%_100%,0_100%)]">
      <p className="m-0 font-funnel text-[6rem] leading-none tracking-[-0.04em] text-black uppercase lg:text-[10rem]">
        {counts && parsed && mounted ? (
          <>
            {parsed.prefix}
            {/*
              The MotionValue is this span's only child — framer drives it straight
              onto textContent, so the figure climbs without re-rendering React.
              Tabular figures stop the number juddering as digits swap.
            */}
            <MotionSpan className="tabular-nums">{display}</MotionSpan>
            {parsed.suffix}
          </>
        ) : (
          stat?.value
        )}
      </p>

      {/* the rule draws out from the left in step with the figure */}
      <MotionDiv
        style={{ scaleX: reveal }}
        className="relative h-[2px] w-full origin-left">
        <DecorationLine
          className="top-0"
          orientation="horizontal"
          style="dark"
        />
      </MotionDiv>

      <p className="m-0 font-geist text-[2.25rem] leading-[1.5] text-black opacity-70 lg:text-[2.5rem]">
        {stat?.label}
      </p>
    </MotionDiv>
  );
}

export default function GoalsSection({ data, className }: { data: PageBastianKellerSchema; className?: string }) {
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const { reduceMotion } = useMotionMode();

  // SPAN_OFFSET rather than the per-block default: these seven blocks are spread
  // down the section's whole height, so the cascade needs that much scroll to play
  const progress = useRevealProgress(sectionRef, SPAN_OFFSET, reduceMotion);

  const stats = data?.goalsStats;
  const total = FIXED_BLOCKS + (stats?.length ?? 0);

  // called unconditionally and in a fixed order: the blocks below are each
  // conditional on content, but the hooks behind them cannot be
  const chip = useReveal(progress, 0, total, reduceMotion);
  const statement = useReveal(progress, 1, total, reduceMotion);
  const text = useReveal(progress, 2, total, reduceMotion);
  const cta = useReveal(progress, 3, total, reduceMotion);

  return (
    <section className={cn("relative z-[99] bg-[#F3F2F1] overflow-clip", className)}>
      <div
        ref={sectionRef}
        className="relative mx-auto w-[min(240rem,100%)] lg:h-[119.375rem]">
        {/* the mobile frame runs the guides 1rem in from each edge, the desktop one 7rem */}
        <DecorationLine
          className="left-[1rem] lg:left-[7.125rem]"
          orientation="vertical"
          style="dark"
        />
        <DecorationLine
          className="right-[1rem] lg:right-[7rem]"
          orientation="vertical"
          style="dark"
        />

        <div className="flex flex-col gap-[2rem] px-[2.25rem] py-[12rem] lg:flex-row lg:gap-[57.125rem] lg:px-[12.125rem] lg:pt-[14.25rem] lg:pb-0">
          {data?.goalsChip?.text && (
            <MotionDiv
              style={{ opacity: chip.reveal, y: chip.y }}
              className="shrink-0 self-start">
              <Chip
                image={data?.goalsChip?.icon}
                text={data?.goalsChip?.text}
              />
            </MotionDiv>
          )}

          <div className="flex flex-col gap-[3.25rem] lg:w-[132.625rem] lg:gap-[8rem]">
            <div className="flex flex-col items-start gap-[3.25rem] lg:gap-[5.125rem]">
              <div className="flex flex-col gap-0 pr-[4rem] lg:gap-[2.5rem] lg:pr-0">
                {data?.goalsStatement && (
                  /*
                    The statement reads itself in word by word off the un-eased
                    slice — a fade you are meant to watch travel across the line,
                    so it takes the linear track for the same reason the counters
                    do. The block still rises with the cascade; opacity is the
                    words' own.
                  */
                  <MotionDiv style={{ y: statement.y }}>
                    <RevealText
                      value={data.goalsStatement}
                      progress={statement.linear}
                      className="pixel-highlight prose-p:font-geist prose-p:text-[3rem] prose-p:leading-[1.2] prose-p:text-black lg:prose-p:text-[5rem] prose-em:!px-0 prose-em:font-ivy lg:prose-em:!px-[.7rem]"
                    />
                  </MotionDiv>
                )}

                {data?.goalsText && (
                  <MotionDiv
                    style={{ opacity: text.reveal, y: text.y }}
                    className="richtext richtext-start lg:w-[78.625rem] prose-p:font-geist prose-p:text-[3rem] prose-p:leading-[1.2] prose-p:text-black/50 lg:prose-p:text-[2.5rem] lg:prose-p:leading-[1.5] lg:prose-p:text-black/70">
                    <PortableText
                      value={data.goalsText}
                      components={richTextComponent}
                    />
                  </MotionDiv>
                )}
              </div>

              {data?.goalsCTA && (
                <MotionDiv
                  style={{ opacity: cta.reveal, y: cta.y }}
                  /*
                    The button is drawn at 48px on the mobile frame against 56px on
                    the desktop one, and every part of it scales with that — the
                    same set the nav's mobile menu overrides.
                  */
                  className="w-full lg:w-auto max-lg:[&_.corner-button]:min-h-[6rem] max-lg:[&_.corner-button]:gap-[1.714rem] max-lg:[&_.corner-button]:px-[3.429rem] max-lg:[&_.corner-button]:text-[1.929rem] max-lg:[&_.corner-button-divider]:h-[3.429rem] max-lg:[&_.corner-button-icon-hover]:h-[2.571rem] max-lg:[&_.corner-button-icon-hover]:w-[2.571rem] max-lg:[&_.corner-button-icon-initial]:h-[2.571rem] max-lg:[&_.corner-button-icon-initial]:w-[2.571rem]">
                  <CTACornerButton data={data.goalsCTA} />
                </MotionDiv>
              )}
            </div>

            {stats && stats.length > 0 && (
              <div className="flex flex-col gap-[1.25rem] lg:flex-row lg:gap-[1rem]">
                {stats.map((stat, index) => (
                  <StatCard
                    key={index}
                    stat={stat}
                    progress={progress}
                    index={FIXED_BLOCKS + index}
                    total={total}
                    reduceMotion={reduceMotion}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* on the section's edge below lg, where the showreel ends; 40px in on the desktop frame */}
      <DecorationLine
        className="top-0 lg:top-[5rem]"
        orientation="horizontal"
        style="dark"
      />
      <DecorationLine
        className="bottom-0"
        orientation="horizontal"
        style="dark"
      />
    </section>
  );
}
